import { Row, Col } from 'react-dsgov';
import styles from './itemReceitaExportacoes.module.scss';
import { formatCurrency } from '../../../../../../../../../../../utils/formatCurrency';
import {
  IReceitaExportacoesNCM,
  IReceitaExportacoesVinculadaNCM,
} from '../../../../../../../../../../../interfaces/IReceitaExportacoes';
import { formatNumber } from '../../../../../../../../../../../utils/formatNumber';
import { BrDivider } from '@govbr-ds/react-components';

interface IItemReceitaCalculadaExportacoesProps {
  itemNCM: IReceitaExportacoesNCM | IReceitaExportacoesVinculadaNCM;
}

const ItemReceitaCalculadaExportacoes = ({ itemNCM }: IItemReceitaCalculadaExportacoesProps) => {
  const hasExportacao = itemNCM?.hasExportacao;
  const NAO_SE_APLICA = 'Não se aplica';

  const htmlMetodoApuracaoPreco = () => {
    const ncmVinculada = itemNCM as IReceitaExportacoesVinculadaNCM;

    return (
      <Col>
        <span className={styles.label}>Método para Apuração do Preço</span>
        <p className={styles.text}>{ncmVinculada?.apuracao?.metodo}</p>
      </Col>
    );
  };

  const htmlPrecoParametroOuValorProducao = () => {
    if (hasExportacao) {
      const ncmVinculada = itemNCM as IReceitaExportacoesVinculadaNCM;
      return (
        <Col>
          <span className={styles.label}>Preço Parâmetro (R$)</span>
          <p className={styles.text}>{formatCurrency(ncmVinculada?.precoParametro)}</p>
        </Col>
      );
    } else {
      const ncmSemVinculacao = itemNCM as IReceitaExportacoesNCM;
      return (
        <Col>
          <span className={styles.label}>Valor de Produção (R$)</span>
          <p className={styles.text}>{formatCurrency(ncmSemVinculacao?.valorProducaoTotal)}</p>
        </Col>
      );
    }
  };

  const htmlSemValidacao = () => {
    const ncmSemVinculacao = itemNCM as IReceitaExportacoesNCM;
    return (
      <Row>
        <Col>
          <span className={styles.label}>% Teor Concentrado (TC)</span>
          <p className={styles.text}>{ncmSemVinculacao?.teorConcentrado?.toString().replace('.', ',')}</p>
        </Col>

        <Col>
          <span className={styles.label}>% Teor Alimentação (TA)</span>
          <p className={styles.text}>{ncmSemVinculacao?.teorAlimentacao?.toString().replace('.', ',')}</p>
        </Col>
        <Col>
          <span className={styles.label}>Substância (Fatores de Ajuste)</span>
          <p className={styles.text}>{ncmSemVinculacao?.substanciaFatorAjuste?.nome ?? '-'}</p>
        </Col>
      </Row>
    );
  };

  const htmlBaseCalculo = () => {
    if (hasExportacao) {
      const ncmVinculada = itemNCM as IReceitaExportacoesVinculadaNCM;
      return (
        <div className={styles.infoContainer}>
          <div>
            <span className={styles.label}>Preço Utilizado (R$)</span>
            <p className={styles.text}>
              Preço {ncmVinculada?.descricaoPrecoUtilizado}: {formatCurrency(ncmVinculada?.precoUtilizado)}
            </p>
          </div>
          <div>
            <span className={styles.label}>Receita Calculada (R$)</span>
            <p className={styles.text}>{formatCurrency(ncmVinculada?.valorReceitaCalculada)}</p>
          </div>
          <div>
            <span className={styles.label}>Deduções (R$)</span>
            <p className={styles.text}>{formatCurrency(ncmVinculada?.valorDeducoes ?? '')}</p>
          </div>

          <div>
            <span className={styles.label}>Base de Cálculo (R$)</span>
            <p className={styles.text}>{formatCurrency(ncmVinculada?.valorBaseCalculo)}</p>
          </div>
        </div>
      );
    } else {
      const ncmSemVinculacao = itemNCM as IReceitaExportacoesNCM;
      return (
        <div className={styles.infoContainer}>
          <div>
            <span className={styles.label}>Índice de Enriquecimento</span>
            <p className={styles.text}>
              {ncmSemVinculacao?.indiceEnriquecimento ? ncmSemVinculacao?.indiceEnriquecimento : NAO_SE_APLICA}
            </p>
          </div>
          <div>
            <span className={styles.label}>Fator de Ajuste</span>
            <p className={styles.text}>
              {ncmSemVinculacao?.fatorAjuste
                ? ncmSemVinculacao?.fatorAjuste?.toString().replace('.', ',')
                : NAO_SE_APLICA}
            </p>
          </div>
          <div>
            <span className={styles.label}>Valor de Referência Unitário (R$)</span>
            <p className={styles.text}>
              {ncmSemVinculacao?.valorReferenciaUnitario
                ? formatCurrency(ncmSemVinculacao?.valorReferenciaUnitario)
                : NAO_SE_APLICA}
            </p>
          </div>
          <div>
            <span className={styles.label}>Valor Utilizado (R$)</span>
            <p className={styles.text}>
              {ncmSemVinculacao?.descricaoValorUtilizado === 'Praticado' ? 'Preço' : 'Valor'}{' '}
              {ncmSemVinculacao?.descricaoValorUtilizado}: {formatCurrency(ncmSemVinculacao?.valorUtilizado)}
            </p>
          </div>
          <div>
            <span className={styles.label}>Receita Calculada (R$)</span>
            <p className={styles.text}>{formatCurrency(ncmSemVinculacao?.valorReceitaCalculada)}</p>
          </div>
          <div>
            <span className={styles.label}>Deduções (R$)</span>
            <p className={styles.text}>{formatCurrency(ncmSemVinculacao?.valorDeducoes ?? '')}</p>
          </div>
          <div>
            <span className={styles.label}>Base de Cálculo (R$)</span>
            <p className={styles.text}>{formatCurrency(ncmSemVinculacao?.valorBaseCalculo)}</p>
          </div>
        </div>
      );
    }
  };

  const htmlTributos = () => {
    if (itemNCM.tributosValores && itemNCM.tributosValores.length > 0) {
      return (
        <>
          <Row>
            {itemNCM.tributosValores?.map((item) => (
              <Col key={item.nome} className={styles.tributosContainer}>
                <span className={styles.label}>{item.nome}</span>
                <p className={styles.text}>{formatCurrency(item.valor || '')}</p>
              </Col>
            ))}
          </Row>
          <div className={styles.metodoDivider}>
            <BrDivider />
          </div>
        </>
      );
    }
    return;
  };

  return (
    <>
      <Row className={styles.row}>
        <Col>
          <span className={styles.label}>
            Há operação de exportação com Pessoa Vinculada/Interposta/País com Tributação Favorecida?
          </span>
          <p className={styles.text}>{itemNCM?.hasExportacao ? 'Sim' : 'Não'}</p>
        </Col>
        <Col>{itemNCM?.hasExportacao && htmlMetodoApuracaoPreco()}</Col>
      </Row>

      <Row className={styles.row}>
        <Col>
          <span className={styles.label}>Código NCM do Bem Mineral</span>
          <p className={styles.text}>{itemNCM?.ncmBemMineral?.codigo}</p>
        </Col>

        <Col>
          <span className={styles.label}>Total da Operação (R$)</span>
          <p className={styles.text}>{formatCurrency(itemNCM?.valorTotalOperacao)}</p>
        </Col>

        <Col>
          <span className={styles.label}>Quantidade</span>
          <p className={styles.text}>{formatNumber(itemNCM?.quantidade)}</p>
        </Col>
      </Row>

      <Row className={styles.row}>
        <Col>
          <span className={styles.label}>Unidade de Medida</span>
          <p className={styles.text}>{itemNCM?.unidadeMedida}</p>
        </Col>

        <Col>
          <span className={styles.label}>Preço Praticado (R$)</span>
          <p className={styles.text}>{formatCurrency(itemNCM.precoPraticado)}</p>
        </Col>

        {htmlPrecoParametroOuValorProducao()}
      </Row>

      {!hasExportacao && htmlSemValidacao()}

      <Row className={styles.row}>
        <Col>
          <span className={styles.label}>O bem mineral é oriundo de rejeitos?</span>
          <p className={styles.text}>{itemNCM?.isRejeito ? 'Sim' : 'Não'}</p>
        </Col>
        <Col>
          <span className={styles.label}>A substância possui uso específico?</span>
          <p className={styles.text}>{itemNCM?.hasUsoEspecifico ? 'Sim' : 'Não'}</p>
        </Col>
      </Row>

      <div className={styles.movimentacao}>
        {htmlTributos()}

        <div className={styles.infoContainer}>
          <div>
            <span className={styles.label}>Quantidade a abater da aquisição de terceiros</span>
            <p className={styles.text}>{formatNumber(itemNCM?.quantidadeAquisicaoTerceiros)}</p>
          </div>

          <div>
            <span className={styles.label}>Valor total a abater da aquisição de terceiros (R$)</span>
            <p className={styles.text}>{formatCurrency(itemNCM?.valorAquisicaoTerceiros ?? '')}</p>
          </div>
        </div>

        {htmlBaseCalculo()}
      </div>
    </>
  );
};

export default ItemReceitaCalculadaExportacoes;
